/**
 * File: ActivityLogger.tsx
 * Description: Real-time activity logger showing Archie's LLM thinking process and reasoning
 * Author: System
 * Created: 2025-08-09
 * Last Modified: 2025-08-09
 */

import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  LightBulbIcon,
  CogIcon,
  BookOpenIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect, useRef } from 'react';

import {
  GenerationLogEntry,
  LogType,
  LogCategory,
  ActivityLogProps,
} from '../../types/generation';

/**
 * Component that displays real-time LLM thinking logs with simulation capabilities
 */
export const ActivityLogger: React.FC<ActivityLogProps> = ({
  logs,
  isLive,
  maxEntries = 500,
  filters = [],
  onClearLogs,
  onExportLogs,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<LogType[]>(filters);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  
  const logContainerRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef<boolean>(true);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (isLive && autoScroll && logContainerRef.current && shouldScrollRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, isLive, autoScroll]);

  // Handle scroll events to determine if user has scrolled up
  const handleScroll = () => {
    if (logContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
      shouldScrollRef.current = isNearBottom;
      
      if (!isNearBottom && autoScroll) {
        setAutoScroll(false);
      } else if (isNearBottom && !autoScroll) {
        setAutoScroll(true);
      }
    }
  };

  const getLogTypeIcon = (type: LogType) => {
    switch (type) {
      case LogType.THINKING:
        return <LightBulbIcon className="w-4 h-4 text-blue-600" />;
      case LogType.ACTION:
        return <CogIcon className="w-4 h-4 text-purple-600" />;
      case LogType.REFERENCE:
        return <BookOpenIcon className="w-4 h-4 text-green-600" />;
      case LogType.RESULT:
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case LogType.ERROR:
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      default:
        return <ChatBubbleLeftRightIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const getLogTypeColor = (type: LogType): string => {
    switch (type) {
      case LogType.THINKING:
        return 'border-l-blue-500 bg-blue-50';
      case LogType.ACTION:
        return 'border-l-purple-500 bg-purple-50';
      case LogType.REFERENCE:
        return 'border-l-green-500 bg-green-50';
      case LogType.RESULT:
        return 'border-l-green-500 bg-green-50';
      case LogType.ERROR:
        return 'border-l-red-500 bg-red-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getCategoryIcon = (category: LogCategory) => {
    switch (category) {
      case LogCategory.GENERATION:
        return <SparklesIcon className="w-3 h-3" />;
      case LogCategory.ANALYSIS:
        return <ChartBarIcon className="w-3 h-3" />;
      case LogCategory.RESEARCH:
        return <BookOpenIcon className="w-3 h-3" />;
      case LogCategory.VALIDATION:
        return <CheckCircleIcon className="w-3 h-3" />;
      default:
        return <DocumentTextIcon className="w-3 h-3" />;
    }
  };

  // Filter logs based on search and selected filters
  const filteredLogs = logs
    .filter(log => {
      if (selectedFilters.length > 0 && !selectedFilters.includes(log.type as LogType)) {
        return false;
      }
      if (searchTerm && !log.message.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      return true;
    })
    .slice(-maxEntries);

  const toggleFilter = (type: LogType) => {
    setSelectedFilters(prev => 
      prev.includes(type) 
        ? prev.filter(f => f !== type)
        : [...prev, type],
    );
  };

  const toggleExpand = (entryId: string) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit', 
    });
  };

  return (
    <div className="bg-background-surface border border-border-subtle rounded-lg h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="w-5 h-5 text-text-onSurface-strong" />
          <h3 className="font-semibold text-text-onSurface-strong">Archie&apos;s Thinking Process</h3>
          {isLive && (
            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              Live
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={onExportLogs}
            className="p-2 text-text-onSurface-subtle hover:text-text-onSurface-strong hover:bg-background-section rounded-lg transition-colors"
            title="Export logs"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onClearLogs}
            className="p-2 text-text-onSurface-subtle hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Clear logs"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="p-4 border-b border-border-subtle space-y-3 flex-shrink-0">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-onSurface-subtle" />
          <input
            type="text"
            placeholder="Search thinking process..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border-medium rounded-lg bg-background-base text-text-onSurface-strong placeholder-text-onSurface-subtle focus:ring-2 focus:ring-accent-tertiary-strong focus:border-accent-tertiary-strong outline-none"
          />
        </div>

        {/* Type Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <FunnelIcon className="w-4 h-4 text-text-onSurface-subtle" />
          {Object.values(LogType).map((type) => (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedFilters.includes(type)
                  ? 'bg-accent-tertiary-strong text-white'
                  : 'bg-background-section text-text-onSurface-subtle hover:bg-background-base'
              }`}
            >
              {getLogTypeIcon(type)}
              <span className="capitalize">{type}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Logs Container */}
      <div 
        ref={logContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-3 min-h-0"
        style={{ maxHeight: 'calc(100% - 200px)' }}
      >
        {filteredLogs.length === 0 ? (
          <div className="text-center py-8 text-text-onSurface-subtle">
            <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-4 text-text-onSurface-subtle" />
            <p>No thinking process logs to display</p>
            <p className="text-sm mt-1">
              {isLive ? 'Logs will appear here when generation starts' : 'No logs match your current filters'}
            </p>
          </div>
        ) : (
          filteredLogs.map((entry) => (
            <div
              key={entry.id}
              className={`border-l-4 pl-4 py-3 rounded-r-lg transition-all break-words ${getLogTypeColor(entry.type as LogType)}`}
            >
              {/* Entry Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {getLogTypeIcon(entry.type as LogType)}
                  <span className="font-medium text-text-onSurface-strong capitalize truncate">
                    {entry.type.replace('_', ' ')}
                  </span>
                  {entry.category && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-background-base rounded-full text-xs text-text-onSurface-subtle">
                      {getCategoryIcon(entry.category as LogCategory)}
                      <span className="capitalize">{entry.category}</span>
                    </div>
                  )}
                  {entry.sectionId && (
                    <div className="px-2 py-1 bg-accent-tertiary-subtle text-accent-tertiary-strong rounded-full text-xs font-medium">
                      {entry.sectionId.replace('_', ' ')}
                    </div>
                  )}
                </div>
                <span className="text-xs text-text-onSurface-subtle flex-shrink-0 ml-2">
                  {formatTimestamp(entry.timestamp)}
                </span>
              </div>

              {/* Message */}
              <p className="text-text-onSurface-strong mb-2 break-words">{entry.message}</p>

              {/* Thinking Process */}
              {entry.thinkingProcess && (
                <div className="bg-background-base border border-border-subtle rounded-lg p-3 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <LightBulbIcon className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-text-onSurface-strong">Reasoning:</span>
                  </div>
                  <p className="text-sm text-text-onSurface-subtle italic">
                    &quot;{entry.thinkingProcess}&quot;
                  </p>
                </div>
              )}

              {/* References */}
              {entry.references && entry.references.length > 0 && (
                <div className="mb-2">
                  <button
                    onClick={() => toggleExpand(entry.id)}
                    className="flex items-center gap-2 text-sm text-accent-tertiary-strong hover:text-accent-tertiary-strong font-medium"
                  >
                    <BookOpenIcon className="w-4 h-4" />
                    <span>{entry.references.length} Reference{entry.references.length !== 1 ? 's' : ''}</span>
                  </button>
                  
                  {expandedEntries.has(entry.id) && (
                    <div className="mt-2 space-y-2">
                      {entry.references.map((ref, index) => (
                        <div key={index} className="bg-background-base border border-border-subtle rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-text-onSurface-strong text-sm">{ref.title}</span>
                            <span className="text-xs text-text-onSurface-subtle">
                              {Math.round(ref.confidence)}% relevance
                            </span>
                          </div>
                          <p className="text-sm text-text-onSurface-subtle">{ref.excerpt}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Confidence Score */}
              {entry.confidence !== undefined && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-text-onSurface-subtle">Confidence:</span>
                  <div className="flex items-center gap-1">
                    <div className="w-16 h-2 bg-background-base border border-border-subtle rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          entry.confidence >= 80 ? 'bg-green-500' :
                            entry.confidence >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${entry.confidence}%` }}
                      />
                    </div>
                    <span className="text-text-onSurface-strong font-medium">{entry.confidence}%</span>
                  </div>
                </div>
              )}

              {/* Agent Name */}
              {entry.agentName && (
                <div className="mt-2 text-xs text-text-onSurface-subtle">
                  Agent: {entry.agentName}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Auto-scroll indicator */}
      {!autoScroll && isLive && (
        <div className="p-2 bg-yellow-50 border-t border-yellow-200 flex-shrink-0">
          <button
            onClick={() => {
              setAutoScroll(true);
              if (logContainerRef.current) {
                logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
              }
            }}
            className="w-full text-center text-sm text-yellow-800 hover:text-yellow-900 font-medium"
          >
            New logs available - Click to scroll to bottom
          </button>
        </div>
      )}
    </div>
  );
};
